/**
 * @file init-queue-mixin.ts
 *
 * Manages the post-configurator-init task queue. Operations enqueued before
 * the configurator is ready are held and executed sequentially once
 * initialization completes.
 *
 * Also locks as-step-selector behind an as-app-busy event while the
 * configurator is initializing, releasing it when the queue drains.
 *
 * The signal is 'as-intrn-configurator-ready', which <as-configurator>
 * dispatches once it has bound its Paramate instance.
 *
 * Exposes: isInitialized, addToPostInitQueue
 */
import { type Constructor } from './mixin-types-utils.ts';
import { _setSysData } from '../../../store/sysDataStore.ts';
import { asConfigurator } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';

/** Surface added by InitQueueMixin. Referenced by ProjectMixin and TransferMixin
 *  which enqueue operations that must run after configurator init. */
export interface InitQueueMixinMembers {
  isInitialized: boolean;
  /** Runs operation immediately if already initialized; queues it otherwise. */
  addToPostInitQueue(operation: () => void): void;
}

export const InitQueueMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class InitQueueMixin extends Base {
    isInitialized: boolean = false;
    postInitQueue: Array<() => void> = [];
    _stepSelectorLockedForInit = false;

    _handleConfiguratorInitialized = async (): Promise<void> => {
      this.isInitialized = true;
      await this._execPostInitQueue();
      // The app is usable from here — sysData.appReady is what a binding waits
      // on, since nothing else broadcasts "the init queue has drained".
      _setSysData({ appReady: true });
      if (this._stepSelectorLockedForInit) {
        this._stepSelectorLockedForInit = false;
        sendEv(window, 'as-app-busy-request', { detail: { key: 'init', busy: false } });
      }
    };

    public addToPostInitQueue(operation: () => void) {
      if (this.isInitialized) {
        operation();
      } else {
        this.postInitQueue.push(operation);
      }
    }

    _execPostInitQueue = async (): Promise<void> => {
      while (this.postInitQueue.length > 0) {
        const operation = this.postInitQueue.shift();
        if (operation) await operation();
      }
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      window.addEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized as EventListener);

      // Delay initial lock to ensure BusyMixin and StepSelector are ready to receive it
      setTimeout(() => {
        if (this.querySelector('as-step-selector') && asConfigurator()) {
          this._stepSelectorLockedForInit = true;
          sendEv(window, 'as-app-busy-request', { detail: { key: 'init', busy: true } });
        }
      }, 200);
    }

    disconnectedCallback() {
      window.removeEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized as EventListener);
      super.disconnectedCallback?.();
    }
  };
